<template>
  <div
    v-if="organization && organization.externalIdentityProviderData"
    class="mt-7 flex-col gap-4"
  >
    <Button
      :label="
        organization.externalIdentityProviderData?.loginButtonLabel ||
        'Sign with SSO'
      "
      :left-image-url="organization.iconUrl ?? undefined"
      :variant="ButtonVariant.OUTLINED"
      block
      class="mt-7"
      @click="handleClick"
    />
  </div>
</template>
<script lang="ts" setup>
import { Button, ButtonVariant } from '@exfluential/ui';
import {
  OAuthProvider,
  SAMLAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  UserCredential,
  signInWithCredential,
  fetchSignInMethodsForEmail,
  signInWithCustomToken,
  linkWithCredential,
} from 'firebase/auth';
import { useFirebase } from '~/composables/use-firebase';
import { useSsoRedirect } from '~/composables/use-sso-redirect';
import { useOrganizationStore } from '~/store/organization';
import { storeToRefs } from 'pinia';
import { useAuthUserStore } from '~/store/auth-user';
import {
  apiGetLoggedInUserInfo,
  apiLogin,
  apiMigrateSsoProvider,
  apiResolveSsoEmailConflict,
} from '~/api/auth';
import { getErrorStatus } from '~/api';
import { usePostHog } from '~/composables/usePostHog';
import { useAccountStore } from '~/store/account';
import { FirebaseError } from 'firebase/app';
import { navigateTo } from '#imports';
import { getFirstQueryParam } from '~/utils/common';
import { resolveDefaultLandingUrl } from '~/composables/use-default-landing-url';

// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
interface ExtendedUserCredential extends UserCredential {
  _tokenResponse?: {
    rawUserInfo: string;
  };
}

const emit = defineEmits<{
  linkAccounts: [email: string];
  error: [err: unknown];
}>();

const route = useRoute();
const { auth } = useFirebase();

const authUserStore = useAuthUserStore();
const accountStore = useAccountStore();
const organizationStore = useOrganizationStore();
const { organization } = storeToRefs(organizationStore);
const { capture } = usePostHog();

const completeSignIn = async () => {
  if (!organization.value) throw new Error('Unknown organization');
  authUserStore.authUser = await apiGetLoggedInUserInfo();
  await accountStore.setCurrentAccount();

  const { identifyUser } = await import('~/utils/posthog');
  if (authUserStore.authUser) {
    const providerType =
      organization.value?.externalIdentityProviderData?.providerId || 'sso';
    identifyUser(authUserStore.authUser, `sso_${providerType}`);
  }
  const redirect = getFirstQueryParam(route.query.redirect);

  if (
    organization.value.isBusinessesEnabled &&
    organization.value.organizationSettings?.businessSelectorEnabled
  ) {
    navigateTo('/business-selection');
  } else {
    navigateTo(redirect ?? resolveDefaultLandingUrl(organization.value));
  }
};

const login = async (
  idToken: string,
  rawUserInfo?: Record<string, unknown>,
) => {
  if (!organization.value) throw new Error('Unknown organization');
  await apiLogin(idToken, rawUserInfo);
  await completeSignIn();
};

const rebuildCredentialWithPendingToken = (
  credential: any,
  pendingToken?: string,
) => {
  if (!pendingToken) {
    return credential;
  }

  const credentialJson = { ...credential.toJSON(), pendingToken };

  return String(credential.providerId).startsWith('saml.')
    ? SAMLAuthProvider.credentialFromJSON(credentialJson)
    : OAuthProvider.credentialFromJSON(credentialJson);
};

const handleSsoMigration = async (email: string, credential: any) => {
  try {
    const { customToken, pendingToken } =
      await apiMigrateSsoProvider(credential);

    const userCredential = await signInWithCustomToken(auth, customToken);
    let rawUserInfo: Record<string, unknown> | undefined;

    try {
      const linkResult: ExtendedUserCredential = await linkWithCredential(
        userCredential.user,
        rebuildCredentialWithPendingToken(credential, pendingToken),
      );
      // eslint-disable-next-line no-underscore-dangle
      const linkedUserInfo = linkResult._tokenResponse?.rawUserInfo;

      if (linkedUserInfo) {
        rawUserInfo = JSON.parse(linkedUserInfo);
      }
    } catch (err: unknown) {
      const isAlreadyLinked =
        err instanceof FirebaseError &&
        err.code === 'auth/provider-already-linked';

      capture('sso_migration_link_error', {
        error_code: err instanceof FirebaseError ? err.code : undefined,
        error_message: err instanceof Error ? err.message : String(err),
        organization_slug: organization.value?.slug,
        email,
        is_already_linked: isAlreadyLinked,
      });

      if (!isAlreadyLinked) {
        throw err;
      }
    }

    const idToken = await userCredential.user.getIdToken();
    await login(idToken, rawUserInfo);
  } catch (err: unknown) {
    capture('sso_migration_error', {
      error_message: err instanceof Error ? err.message : String(err),
      error_code: err instanceof FirebaseError ? err.code : undefined,
      organization_slug: organization.value?.slug,
      email,
    });

    emit(
      'error',
      new Error('Unable to migrate your account. Please contact support.'),
    );
  }
};

const handleSharedEmailConflict = async (email: string, credential: any) => {
  const { resolution, customToken } =
    await apiResolveSsoEmailConflict(credential);

  capture('sso_email_conflict', {
    resolution,
    organization_slug: organization.value?.slug,
  });

  if (resolution === 'separate_account' && customToken) {
    await signInWithCustomToken(auth, customToken);
    await completeSignIn();
    return;
  }

  if (resolution === 'link_account') {
    authUserStore.setCredentialToLinkOnLogin(credential);
    emit('linkAccounts', email);
    return;
  }

  await handleSsoMigration(email, credential);
};

const SSO_IDENTITY_CONFLICT_STATUS = 409;

const signInWithProvider = async () => {
  if (!organization.value?.externalIdentityProviderData?.providerId) {
    throw new Error('External identity provider id not found');
  }

  const { providerId } = organization.value.externalIdentityProviderData;
  const provider = providerId.startsWith('saml.')
    ? new SAMLAuthProvider(providerId)
    : new OAuthProvider(providerId);

  const useRedirectFlow =
    organization.value.externalIdentityProviderData.useRedirectFlow ?? false;

  if (useRedirectFlow) {
    const ssoRedirect = useSsoRedirect();
    const redirect = getFirstQueryParam(route.query.redirect);

    ssoRedirect.saveState({
      organizationSlug: organization.value.slug,
      returnUrl: redirect || resolveDefaultLandingUrl(organization.value),
    });

    globalThis.history.replaceState({}, '', '/auth/sso/callback');
    await signInWithRedirect(auth, provider);
    return;
  }

  try {
    const result: ExtendedUserCredential = await signInWithPopup(
      auth,
      provider,
    );

    const rawUserInfo = JSON.parse(
      // eslint-disable-next-line no-underscore-dangle
      result._tokenResponse?.rawUserInfo || '{}',
    );

    const idToken = await result.user.getIdToken();

    try {
      await login(idToken, rawUserInfo);
    } catch (loginErr: unknown) {
      const credential = providerId.startsWith('saml.')
        ? SAMLAuthProvider.credentialFromResult(result)
        : OAuthProvider.credentialFromResult(result);
      const isSignedInAsAnotherIdentity =
        organization.value?.externalIdentityProviderData
          ?.identifiesByUsername &&
        getErrorStatus(loginErr) === SSO_IDENTITY_CONFLICT_STATUS;

      if (!isSignedInAsAnotherIdentity || !credential) {
        throw loginErr;
      }

      await handleSharedEmailConflict(result.user.email ?? '', credential);
    }
  } catch (err: unknown) {
    if (
      err instanceof FirebaseError &&
      err.code === 'auth/account-exists-with-different-credential'
    ) {
      const credential = providerId.startsWith('saml.')
        ? SAMLAuthProvider.credentialFromError(err)
        : OAuthProvider.credentialFromError(err);

      if (credential && err.customData?.email) {
        if (
          organization.value?.externalIdentityProviderData?.identifiesByUsername
        ) {
          await handleSharedEmailConflict(
            err.customData.email as string,
            credential,
          );
          return;
        }

        const providers = await fetchSignInMethodsForEmail(
          auth,
          err.customData.email as string,
        );
        const hasOnlyOidcProviders =
          providers.length > 0 &&
          providers.every((p) => p.startsWith('oidc.')) &&
          !providers.includes('password');

        if (
          hasOnlyOidcProviders &&
          organization.value?.externalIdentityProviderData
        ) {
          await handleSsoMigration(err.customData.email as string, credential);
          return;
        }

        if (!providers.includes('password')) {
          const result = await signInWithCredential(auth, credential);
          const rawUserInfo = JSON.parse(
            // eslint-disable-next-line no-underscore-dangle
            (result as ExtendedUserCredential)._tokenResponse?.rawUserInfo ||
              '{}',
          );
          const idToken = await result.user.getIdToken();
          await login(idToken, rawUserInfo);
          return;
        }

        authUserStore.setCredentialToLinkOnLogin(credential);
        emit('linkAccounts', err.customData.email as string);
        return;
      }
    }

    emit('error', err);
  }
};

const handleClick = () =>
  useLoader({
    action: signInWithProvider,
    onError(error: any) {
      if (error?.message === 'Login cancelled') return;
      emit('error', error);
    },
  });
</script>
